import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Tag, ArrowLeft, Newspaper } from "lucide-react";
import { api, resolveMedia } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import Seo, { SITE_URL } from "../components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const { pick } = useLang();
  const [blog, setBlog] = useState(null);
  const [latest, setLatest] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setBlog(null);
    setNotFound(false);
    api.get(`/blogs/${slug}`).then((r) => setBlog(r.data)).catch(() => setNotFound(true));
    api.get("/blogs?limit=4").then((r) => setLatest((r.data.items || []).filter((b) => b.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  const articleSchema = blog
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: blog.title,
        description: blog.seo_description || blog.title,
        image: blog.featured_image ? (blog.featured_image.startsWith("http") ? blog.featured_image : `${SITE_URL}${blog.featured_image}`) : undefined,
        datePublished: blog.published_at,
        dateModified: blog.updated_at || blog.published_at,
        inLanguage: "hi",
        author: { "@type": "Organization", name: blog.author || "Anansha Welfare Society", url: SITE_URL },
        publisher: {
          "@type": "NGO",
          name: "Anansha Welfare Society",
          logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/logo.jpg` },
        },
        mainEntityOfPage: `${SITE_URL}/blog/${blog.slug}`,
      }
    : null;

  return (
    <main className="bg-ngo-cream min-h-screen">
      {blog && (
        <Seo
          title={blog.seo_title || blog.title}
          description={blog.seo_description || blog.title}
          path={`/blog/${blog.slug}`}
          image={blog.featured_image}
          type="article"
          breadcrumb={["ब्लॉग", blog.title]}
          jsonLd={articleSchema}
        />
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm font-semibold text-ngo-deep hover:gap-3 transition-[gap] duration-200">
          <ArrowLeft size={15} /> {pick({ hi: "सभी ब्लॉग", en: "All Blogs" })}
        </Link>

        {notFound && <p data-testid="blog-not-found" className="mt-16 text-center text-ngo-ink/60">{pick({ hi: "यह ब्लॉग उपलब्ध नहीं है।", en: "This blog is not available." })}</p>}
        {!blog && !notFound && <p className="mt-16 text-center text-ngo-ink/50">...</p>}

        {blog && (
          <article data-testid="blog-detail">
            <Reveal>
              <h1 className="mt-6 font-heading font-black text-3xl sm:text-5xl tracking-tight leading-tight text-ngo-ink">{blog.title}</h1>
            </Reveal>
            <Reveal i={1}>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-ngo-ink/55">
                {blog.published_at && <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} /> {new Date(blog.published_at).toLocaleDateString("hi-IN", { day: "numeric", month: "long", year: "numeric" })}</span>}
                {blog.category && <span className="inline-flex items-center gap-1.5 text-ngo-leaf font-semibold"><Tag size={14} /> {blog.category}</span>}
              </div>
            </Reveal>
            {blog.featured_image && (
              <Reveal i={2}>
                <img src={resolveMedia(blog.featured_image)} alt={blog.image_alt || blog.title} title={blog.image_alt || blog.title} className="mt-8 w-full rounded-3xl object-cover max-h-[460px] border border-ngo-deep/10" />
              </Reveal>
            )}
            <Reveal i={3}>
              <div
                data-testid="blog-content"
                className="mt-8 text-base md:text-lg leading-relaxed text-ngo-ink/80 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_blockquote]:border-l-4 [&_blockquote]:border-ngo-gold [&_blockquote]:pl-4 [&_blockquote]:italic [&_a]:text-ngo-deep [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </Reveal>
            {blog.tags?.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {blog.tags.map((tag) => (
                  <span key={tag} className="px-3.5 py-1.5 rounded-full bg-ngo-mint text-ngo-deep text-xs font-semibold">#{tag}</span>
                ))}
              </div>
            )}
          </article>
        )}

        {latest.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading font-bold text-2xl text-ngo-ink mb-6">{pick({ hi: "अन्य नवीनतम ब्लॉग", en: "More Latest Blogs" })}</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {latest.map((b, i) => (
                <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`related-blog-${i}`} className="group bg-white rounded-2xl border border-ngo-deep/10 overflow-hidden hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(23,107,58,0.12)] transition-[box-shadow,transform] duration-300">
                  {b.featured_image ? (
                    <img src={resolveMedia(b.featured_image)} alt={b.title} loading="lazy" className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-ngo-mint flex items-center justify-center text-ngo-deep/40"><Newspaper size={26} /></div>
                  )}
                  <p className="p-4 font-semibold text-sm text-ngo-ink leading-snug group-hover:text-ngo-deep transition-colors duration-200">{b.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
